/**
 * Generalized Delivery Date Monitor
 *
 * A configurable module for monitoring delivery date discrepancies across different pages.
 * Supports custom page mappings, data expiry, and pluggable integration.
 */

/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */

import { track } from "jiffy-analytics";
import debounce from "lodash.debounce";

// Default configuration
const DEFAULT_CONFIG = {
  apiEndpoints: [
    "/api/made/current_pdp_date",
    "/api/v2/estimates/composition_dates.json",
    "/api/v2/blanks/delivery_estimates.json",
    "/api/v2/print/delivery_estimates.json",
    "/api/v2/transfers/delivery_estimates.json",
    "/api/products/{product-slug}/delivery_estimates/current_pdp_date",
    "/api/v2/cart/delivery.json"
  ], // Now supports multiple endpoints including patterns
  storageKey: "delivery_date_monitor",
  expiryHours: 6,
  enableMixpanel: true,
  mixpanelEventName: "delivery_date_discrepancy",
  logLevel: "info", // "debug", "info", "warn", "error"
  pagePatterns: {
    // Design Lab (must come before PLP to catch special cases)
    "/design-lab": "DesignLab",
    "/design-lab*": "DesignLab",
    "/custom-printed-shirts*bare=true*": "DesignLab",

    // PDPs
    ".html": "PDP",

    // Cart
    "/cart/buy": "Cart",
    "/cart": "Cart",

    // PLPs
    "/custom-printed-shirts*": "PLP",
    "/custom-printed/t-shirts*": "PLP",
    "/polo-shirts*": "PLP",
    "/transfers*": "PLP",
    "/sweatshirts*": "PLP",
    "/tshirts*": "PLP",
    "category=": "PLP",

    // Checkout
    "/checkout": "Checkout",

    // Home (exact root only)
    "/": "Home"
  }
};

const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Logger utility with configurable levels
 */
const createLogger = (level = "info") => {
  const levels = { debug: 0, info: 1, warn: 2, error: 3 };
  const currentLevel = levels[level] || 1;

  return {
    debug(...args) {
      if (currentLevel <= levels.debug) {
        console.debug("[DeliveryMonitor]", ...args);
      }
    },

    info(...args) {
      if (currentLevel <= levels.info) {
        console.info("[DeliveryMonitor]", ...args);
      }
    },

    warn(...args) {
      if (currentLevel <= levels.warn) {
        console.warn("[DeliveryMonitor]", ...args);
      }
    },

    error(...args) {
      if (currentLevel <= levels.error) {
        console.error("[DeliveryMonitor]", ...args);
      }
    }
  };
};

/**
 * Main Delivery Date Monitor Class
 */
class DeliveryDateMonitor {
  constructor(userConfig = {}) {
    // Ensure apiEndpoints is always an array
    const config = { ...userConfig };
    if (config.apiEndpoints && !Array.isArray(config.apiEndpoints)) {
      config.apiEndpoints = [config.apiEndpoints];
    }

    this.config = { ...DEFAULT_CONFIG, ...config };
    this.logger = createLogger(this.config.logLevel);
    this.initialized = false;
    this.deliveryData = {};
    this.compiledPagePatterns = this.compilePagePatterns(this.config.pagePatterns);

    // Setup debounced event firing with lodash.debounce
    // Use a Map to store pending events and a single debounced function to process them
    this.pendingEvents = new Map();
    this.debouncedProcessor = debounce(this.processPendingEvents.bind(this), 1000);

    this.init();
  }

  // ============================================================================
  // INITS - Initialization methods
  // ============================================================================

  /**
   * Initialize the monitor
   */
  init() {
    if (this.initialized) return;

    this.logger.info("Initializing delivery date monitor with config:", {
      apiEndpoints: this.config.apiEndpoints,
      expiryHours: this.config.expiryHours,
      pagePatterns: `${Object.keys(this.config.pagePatterns).length} patterns`
    });

    // Load existing data from storage
    this.loadStorageData();

    // Set up API interception
    this.interceptFetch();
    this.interceptXHR();

    this.initialized = true;
    this.logger.info("Delivery date monitor initialized successfully");
  }

  // ============================================================================
  // GETTERS - Methods that provide data without mutation
  // ============================================================================

  /**
   * Get the current page name based on URL patterns
   */
  getPageName(url = window.location.pathname) {
    // Prefer compiled patterns first (preserves insertion order semantics)
    for (const entry of this.compiledPagePatterns) {
      if (entry.regex ? entry.regex.test(url) : url.includes(entry.pattern)) {
        this.logger.debug(
          `Matched URL "${url}" to page "${entry.pageName}" using pattern "${entry.raw}"`
        );
        return entry.pageName;
      }
    }

    this.logger.debug(`No pattern matched for URL "${url}", defaulting to "PLP"`);
    return "PLP";
  }

  /**
   * Precompile page patterns to simple regexes for '*' wildcards, retain includes for plain strings
   */
  // eslint-disable-next-line class-methods-use-this
  compilePagePatterns(pagePatterns) {
    const compiled = [];
    for (const [pattern, pageName] of Object.entries(pagePatterns || {})) {
      // Ensure the home pattern only matches exact root URLs (no path/query/hash)
      if (pattern === "/") {
        compiled.push({
          raw: pattern,
          pageName,
          regex: /^(?:https?:\/\/[^/]+)?\/$/
        });
      } else if (pattern.includes("*")) {
        // Escape regex chars, then turn '*' into '.*'
        const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*");
        compiled.push({ raw: pattern, pageName, regex: new RegExp(escaped) });
      } else {
        compiled.push({ raw: pattern, pageName, pattern, regex: null });
      }
    }
    return compiled;
  }

  /**
   * Get current page information
   */
  getCurrentPageInfo() {
    const { href: url, pathname } = window.location;
    const pageType = this.getPageName();

    return {
      pageType,
      url,
      pathname
    };
  }

  /**
   * Extract zipcode from HTML data-zipcode attribute
   * @returns {string|null} The zipcode or null if not found
   */
  extractZipcodeFromDOM() {
    try {
      const $zipcodeElement = document.querySelector("html");
      if ($zipcodeElement) {
        const zipcode = $zipcodeElement.getAttribute("data-zipcode")?.trim() || "";
        if (zipcode) {
          this.logger.debug(`Found zipcode in DOM data-zipcode attribute: ${zipcode}`);
          return zipcode;
        }

        const $zipcodeTextField = document.querySelector(".js-deliver-to-modal-input");
        if ($zipcodeTextField) {
          const textFieldZipcode = $zipcodeTextField.value?.trim() || "";
          if (textFieldZipcode) {
            this.logger.debug(`Found zipcode in DOM text field: ${textFieldZipcode}`);
            return textFieldZipcode;
          }
        }
      }
    } catch (error) {
      this.logger.error("Error extracting zipcode from DOM:", error);
    }
    return null;
  }

  /**
   * Extract store_id from DOM element with id 'order-number-container'
   * @returns {string|null} The store_id or null if not found
   */
  getStoreIdFromDOM() {
    try {
      const $orderNumberContainer = document.getElementById("order-number-container");
      if ($orderNumberContainer) {
        const raw =
          $orderNumberContainer.getAttribute("data-store-id") ||
          $orderNumberContainer.dataset?.storeId;
        if (raw && String(raw).trim()) {
          const storeId = String(raw).trim();
          this.logger.debug(`Found store_id in DOM: ${storeId}`);
          return storeId;
        }
      }
    } catch (error) {
      this.logger.error("Error extracting store_id from DOM:", error);
    }
    return null;
  }

  /**
   * Extract business line from DOM element with mixpanel data
   * @returns {string|null} The business line or null if not found
   */
  getBusinessLineFromDOM() {
    try {
      const $element = document.querySelector(
        '[data-mixpanel-event="see_similar_products_clicked"]'
      );
      if ($element) {
        const { mixpanelData } = $element.dataset;
        if (mixpanelData) {
          const data = JSON.parse(mixpanelData);
          if (data.business_line) {
            this.logger.debug(`Found business_line in DOM: ${data.business_line}`);
            return data.business_line;
          }
        }
      }
    } catch (error) {
      this.logger.error("Error extracting business_line from DOM:", error);
    }
    return null;
  }

  /**
   * Infers the business line with priority:
   * 1. Check URL for /custom-printed-shirts -> "Print"
   * 2. Check URL for ac=Transfer -> "Transfers"
   * 3. Check DOM element with mixpanel data
   * 4. Return empty string (no default)
   */
  inferBusinessLineFromURL(url = window.location.href) {
    // Priority 1: Check for custom printed shirts
    if (url.includes("/custom-printed-shirts")) {
      return "Print";
    }

    // Priority 2: Check for transfers (check full URL for query parameter)
    if (url.includes("ac=Transfer")) {
      return "Transfers";
    }

    // Priority 3: Check DOM element
    const domBusinessLine = this.getBusinessLineFromDOM();
    if (domBusinessLine) {
      return domBusinessLine;
    }

    // No default fallback - return empty string
    return "";
  }

  // ============================================================================
  // ACTIONS - Data processing, handlers, and business logic methods
  // ============================================================================

  /**
   * Parse various date formats into a Date object
   * @param {string} dateStr - Date string in various formats
   * @returns {Date|null} Parsed Date object or null if parsing fails
   */
  parseDeliveryDate(dateStr) {
    if (!dateStr) return null;

    try {
      // Type 1: "Today, Aug. 5" or "Tomorrow, Aug. 5" or "Thursday, Aug. 7" format
      if (dateStr.includes(",")) {
        // Take everything after comma and trim
        const afterComma = dateStr.split(",")[1].trim();

        // Handle dates with space instead of period (e.g., "Aug 7" or "Aug. 7")
        let monthStr;
        let dayStr;
        if (afterComma.includes(".")) {
          // Split by '.' to get month and date
          const parts = afterComma.split(".");
          if (parts.length >= 2) {
            monthStr = parts[0].trim();
            dayStr = parts[1].trim();
          }
        } else {
          // Split by space for formats like "Aug 7"
          const parts = afterComma.split(" ");
          if (parts.length >= 2) {
            monthStr = parts[0].trim();
            dayStr = parts[1].trim();
          }
        }

        if (monthStr && dayStr) {
          // Map month short forms to month index
          const monthIndex = months.findIndex(m =>
            monthStr.toLowerCase().startsWith(m.toLowerCase())
          );

          if (monthIndex !== -1) {
            const currentYear = new Date().getFullYear();
            const day = parseInt(dayStr, 10);

            if (!Number.isNaN(day)) {
              const parsedDate = new Date(currentYear, monthIndex, day);
              this.logger.debug(`Parsed date "${dateStr}" to ${parsedDate.toString()}`);
              return parsedDate;
            }
          }
        }
      }

      // Type 2: "2025-08-10" format date
      if (dateStr.includes("-")) {
        const parts = dateStr.split("-");
        if (parts.length >= 3) {
          const year = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in JS
          const day = parseInt(parts[2], 10);

          if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
            const parsedDate = new Date(year, month, day);
            this.logger.debug(`Parsed date "${dateStr}" to ${parsedDate.toString()}`);
            return parsedDate;
          }
        }
      }

      this.logger.warn(`Unable to parse date: ${dateStr}`);
      return null;
    } catch (error) {
      this.logger.error(`Error parsing date: ${dateStr}`, error);
      return null;
    }
  }

  /**
   * Format date for display purposes
   * @param {Date} date - Date object to format
   * @returns {string} Formatted date string (MMM DD, YYYY)
   */
  // eslint-disable-next-line class-methods-use-this
  formatDeliveryDate(date) {
    if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
      return "";
    }

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec"
    ];
    const month = monthNames[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();

    return `${month} ${day}, ${year}`;
  }

  /**
   * Format date in ISO format for Mixpanel (YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS)
   * @param {Date} date - Date object to format
   * @param {boolean} includeTime - Whether to include time component (default: false)
   * @returns {string} ISO formatted date string
   */
  // eslint-disable-next-line class-methods-use-this
  formatDeliveryDateISO(date, includeTime = false) {
    if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
      return "";
    }
    // 'en-CA' yields YYYY-MM-DD
    const datePart = new Intl.DateTimeFormat("en-CA").format(date);
    if (includeTime) {
      // 'en-GB' yields HH:mm:ss in 24h format
      const timePart = new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      }).format(date);
      return `${datePart}T${timePart}`;
    }
    return datePart;
  }

  /**
   * Ensure a timestamp value is in ISO format
   * Handles: ISO strings, Date objects, or legacy string formats
   * @param {string|Date} value - The timestamp value to format
   * @returns {string} ISO formatted timestamp string or empty string if invalid
   */
  // eslint-disable-next-line class-methods-use-this
  ensureISOFormat(value) {
    if (!value) return "";
    // Already ISO format (e.g., "2025-01-15T10:30:00.000Z" or "2025-01-15T10:30:00")
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
      return value;
    }
    // Date object or parseable string (e.g., legacy "Wed Jan 15 2025 10:30:00 GMT...")
    const dateObj = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(dateObj.getTime())) return "";
    return dateObj.toISOString();
  }

  /**
   * Check if a stored entry has expired
   */
  hasExpired(timestamp) {
    if (!timestamp) return true;

    const expiryMs = this.config.expiryHours * 60 * 60 * 1000;
    const now = Date.now();
    const age = now - timestamp;
    const expired = age > expiryMs;

    this.logger.debug(`Entry age: ${Math.floor(age / 1000 / 60)} minutes, expired: ${expired}`);
    return expired;
  }

  /**
   * Check if URL matches any monitored endpoint (supports patterns)
   * @param {string} url - The URL to check
   * @returns {boolean} True if URL matches any endpoint
   */
  isMonitoredUrl(url) {
    if (typeof url !== "string") return false;

    // Skip tracking composition estimates on /cart/buy
    try {
      if (
        window?.location?.pathname === "/cart/buy" &&
        url.includes("/api/v2/estimates/composition_dates.json")
      ) {
        return false;
      }
    } catch (e) {
      // no-op
    }

    return this.config.apiEndpoints.some(endpoint => {
      // Check if endpoint contains a pattern marker
      if (endpoint.includes("{") && endpoint.includes("}")) {
        // Convert pattern to regex
        // Replace {param} with a regex that matches any non-slash characters
        const regexPattern = endpoint
          .replace(/[.*+?^${}()|[\]\\]/g, "\\$&") // Escape special regex chars
          .replace(/\\\{[^}]+\\\}/g, "[^/]+"); // Replace {param} with regex

        const regex = new RegExp(regexPattern);
        return regex.test(url);
      }
      // For static endpoints, use simple includes check
      return url.includes(endpoint);
    });
  }

  /**
   * Load data from localStorage with expiry check
   */
  loadStorageData() {
    try {
      const stored = localStorage.getItem(this.config.storageKey);
      if (!stored) {
        this.logger.debug("No existing data in localStorage");
        return;
      }

      const parsedData = JSON.parse(stored);
      const validData = {};
      let expiredCount = 0;
      let migratedCount = 0;

      // Filter out expired entries
      for (const [key, entry] of Object.entries(parsedData)) {
        if (!this.hasExpired(entry.lastUpdated)) {
          // Handle array format
          const deserializedEntry = { ...entry };

          // If it's an array, deserialize dates
          if (Array.isArray(deserializedEntry.data)) {
            deserializedEntry.data = deserializedEntry.data.map(item => ({
              ...item,
              date: item.date ? new Date(item.date) : null
            }));
          }

          // Check if key has old "comp_" prefix and migrate
          if (key.startsWith("comp_")) {
            const newKey = key.replace(/^comp_/, "");
            validData[newKey] = deserializedEntry;
            migratedCount += 1;
            this.logger.debug(`Migrated key from "${key}" to "${newKey}"`);
          } else {
            validData[key] = deserializedEntry;
          }
        } else {
          expiredCount += 1;
        }
      }

      this.deliveryData = validData;

      this.logger.info(
        `Loaded ${Object.keys(validData).length} valid entries from storage${
          expiredCount > 0 ? ` (${expiredCount} expired entries removed)` : ""
        }${migratedCount > 0 ? ` (${migratedCount} keys migrated)` : ""}`
      );

      // Save cleaned data back if any entries were removed or migrated
      if (expiredCount > 0 || migratedCount > 0) {
        this.saveStorageData();
      }
    } catch (error) {
      this.logger.error("Failed to load storage data:", error);
      this.deliveryData = {};
    }
  }

  /**
   * Save data to localStorage with timestamp
   */
  saveStorageData() {
    try {
      // Add timestamps to all entries and serialize Date objects
      const dataWithTimestamps = {};
      for (const [key, value] of Object.entries(this.deliveryData)) {
        const serializedEntry = {
          ...value,
          lastUpdated: value.lastUpdated || Date.now()
        };

        // Convert Date objects to ISO strings for JSON serialization
        if (Array.isArray(serializedEntry.data)) {
          serializedEntry.data = serializedEntry.data.map(item => ({
            ...item,
            date: item.date instanceof Date ? item.date.toString() : item.date
          }));
        }

        dataWithTimestamps[key] = serializedEntry;
      }

      localStorage.setItem(this.config.storageKey, JSON.stringify(dataWithTimestamps));
      this.logger.debug(`Saved ${Object.keys(dataWithTimestamps).length} entries to storage`);
    } catch (error) {
      if (error.name === "QuotaExceededError") {
        this.logger.error("LocalStorage quota exceeded:", error);
      } else {
        this.logger.error("Failed to save storage data:", error);
      }
    }
  }

  /**
   * Get composition ID from URL
   */
  static getCompositionId(url) {
    const match = url.match(/composition_id=(\d+)/);
    return match ? match[1] : null;
  }

  /**
   * Extract composition ID from various sources with priority
   * Priority: 1. URL, 2. Request data, 3. Response data, 4. DOM element
   * @param {string} url - The request URL
   * @param {Object} requestData - The request body data (if any)
   * @param {Object} responseData - The response data
   * @returns {string|null} The composition ID or null
   */
  findCompositionId(url, requestData, responseData) {
    // Priority 1: Check URL query parameters
    const urlCompositionId = DeliveryDateMonitor.getCompositionId(url);
    if (urlCompositionId) {
      this.logger.debug(`Found composition_id in URL: ${urlCompositionId}`);
      return urlCompositionId;
    }

    // Priority 2: Check request data (for POST requests)
    if (requestData?.composition_id) {
      this.logger.debug(`Found composition_id in request data: ${requestData.composition_id}`);
      return String(requestData.composition_id);
    }

    // Priority 3: Check response data
    if (responseData) {
      // Check direct composition_id field
      if (responseData.composition_id) {
        this.logger.debug(`Found composition_id in response data: ${responseData.composition_id}`);
        return String(responseData.composition_id);
      }

      // Check nested locations in response
      if (responseData.variant && responseData.variant.composition_id) {
        this.logger.debug(
          `Found composition_id in response variant: ${responseData.variant.composition_id}`
        );
        return String(responseData.variant.composition_id);
      }

      if (responseData.mixpanel && responseData.mixpanel.composition_id) {
        this.logger.debug(
          `Found composition_id in response mixpanel: ${responseData.mixpanel.composition_id}`
        );
        return String(responseData.mixpanel.composition_id);
      }
    }

    // Priority 4: Check DOM element
    const domCompositionId = this.getCompositionIdFromDOM();
    if (domCompositionId) {
      this.logger.debug(`Found composition_id in DOM: ${domCompositionId}`);
      return domCompositionId;
    }

    this.logger.warn("Could not find composition_id from any source");
    return null;
  }

  /**
   * Get variant information from composition_variants_with_quantities
   * @param {string|number} compositionId - The composition ID to look for
   * @param {Object} mixpanelData - The mixpanel object from the response
   * @param {string} businessLine - The business line from the API response
   * @returns {Array} Array of variant objects with variant_id and quantity, or empty array if not found
   */
  getVariantInfo(compositionId, mixpanelData, businessLine) {
    try {
      const compositionIdStr = String(compositionId);
      const variantsWithQuantities = mixpanelData?.composition_variants_with_quantities;

      if (!variantsWithQuantities || typeof variantsWithQuantities !== "object") {
        this.logger.debug("No variant data found in mixpanel.composition_variants_with_quantities");
        return [];
      }

      // For Print business line, filter by compositionId first, then fallback to combining all
      if (String(businessLine || "").toLowerCase() === "print") {
        const variantsForComposition = variantsWithQuantities[compositionIdStr];
        if (Array.isArray(variantsForComposition) && variantsForComposition.length > 0) {
          const filtered = variantsForComposition.filter(v => v != null);
          this.logger.debug(
            `Got variant info for Print composition ${compositionIdStr}: ${filtered.length} variants found`
          );
          return filtered;
        }
        // Fallback: if not found, try combining all (edge case)
        const allVariantArrays = Object.values(variantsWithQuantities).filter(Array.isArray);
        const combined = [].concat(...allVariantArrays).filter(v => v != null);

        if (!Array.isArray(combined) || combined.length === 0) {
          this.logger.debug("No variant arrays found to combine for Print business line");
          return [];
        }

        this.logger.debug(
          `Got combined variant info for Print (fallback): ${combined.length} variants across ${allVariantArrays.length} compositions`
        );
        return combined;
      }

      if (!variantsWithQuantities[compositionIdStr]) {
        this.logger.debug(`No variant data found for composition ${compositionIdStr}`);
        return [];
      }

      const variants = variantsWithQuantities[compositionIdStr];
      if (!Array.isArray(variants) || variants.length === 0) {
        this.logger.debug(`Invalid or empty variants array for composition ${compositionIdStr}`);
        return [];
      }

      // Filter out null/undefined elements before returning
      const filtered = variants.filter(v => v != null);
      this.logger.debug(
        `Got variant info for composition ${compositionIdStr}: ${filtered.length} variants found`
      );

      return filtered;
    } catch (error) {
      this.logger.error("Error getting variant info:", error);
      return [];
    }
  }

  /**
   * Get composition ID from DOM element
   * @returns {string|null} The composition ID or null
   */
  getCompositionIdFromDOM() {
    try {
      const $element = document.getElementById("vue-product-details-dtf");
      if ($element) {
        const { appConfig } = $element.dataset;
        if (appConfig) {
          // Parse the JSON data (it might be HTML-encoded)
          const decodedConfig = appConfig.replace(/&quot;/g, '"');
          const configData = JSON.parse(decodedConfig);

          if (configData.variant && configData.variant.composition_id) {
            return String(configData.variant.composition_id);
          }
        }
      }
    } catch (error) {
      this.logger.error("Error getting composition_id from DOM:", error);
    }
    return null;
  }

  /**
   * Get or create a unique identifier for the current user/session
   */
  static getUserIdentifier() {
    // Try to get user ID from DOM
    const $userElement = document.querySelector("[data-user-id]");
    if ($userElement?.dataset.userId) {
      return { id: $userElement.dataset.userId, type: "user" };
    }

    // Check for guest token
    const [, guestToken] = document.cookie.match(/guest_token=([^;]+)/) || [];
    if (guestToken) {
      return { id: guestToken, type: "guest" };
    }

    // Create or get session ID
    let sessionId = localStorage.getItem("delivery_monitor_session");
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem("delivery_monitor_session", sessionId);
    }

    return { id: sessionId, type: "session" };
  }

  /**
   * Detect the type of API response
   * @returns {string} 'single' | 'multi' | 'cart' | 'unknown'
   */
  // eslint-disable-next-line class-methods-use-this
  detectResponseType(responseData) {
    // Check for cart delivery response
    if (responseData.shipments && responseData.delivery && responseData.delivery_notification) {
      return "cart";
    }

    // Check for multi-composition response (new format)
    if (responseData.success !== undefined && responseData.estimates) {
      return "multi";
    }

    // Check for single composition response (existing format)
    if (responseData.composition_dates) {
      return "single";
    }

    return "unknown";
  }

  /**
   * Process API response and check for discrepancies
   */
  processDeliveryResponse(url, responseData, requestData = null) {
    const responseType = this.detectResponseType(responseData);

    this.logger.debug(`Detected response type: ${responseType}`);

    if (responseType === "single") {
      this.processSingleCompositionResponse(url, responseData, { requestData });
    } else if (responseType === "multi") {
      this.processMultiCompositionResponse(url, responseData);
    } else if (responseType === "cart") {
      this.processCartDeliveryResponse(url, responseData);
    } else {
      this.logger.warn("Unknown response format, skipping processing");
    }
  }

  /**
   * Convert variants_with_quantities object to variant_info array format
   * @param {Object} variantsWithQuantities - Object like {"54769": 9, "54770": 9}
   * @returns {Array} Array of {variant_id, quantity} objects, sorted by variant_id for consistent comparison
   */
  // eslint-disable-next-line class-methods-use-this
  convertVariantsToVariantInfo(variantsWithQuantities) {
    if (!variantsWithQuantities) return [];

    let variantInfo = [];

    if (Array.isArray(variantsWithQuantities)) {
      for (const obj of variantsWithQuantities) {
        if (obj && typeof obj === "object") {
          for (const [variantId, quantity] of Object.entries(obj)) {
            variantInfo.push({
              variant_id: parseInt(variantId, 10),
              quantity: parseInt(quantity, 10)
            });
          }
        }
      }
    } else if (typeof variantsWithQuantities === "object") {
      variantInfo = Object.entries(variantsWithQuantities).map(([variantId, quantity]) => ({
        variant_id: parseInt(variantId, 10),
        quantity: parseInt(quantity, 10)
      }));
    } else {
      return [];
    }

    // Sort by variant_id for consistent comparison
    return variantInfo.sort((a, b) => a.variant_id - b.variant_id);
  }

  /**
   * Find compositions in stored data that have matching variant_info (exact match: same variants + same quantities)
   * @param {Array} cartVariantInfo - variant_info array from cart response
   * @returns {Array} Array of {compositionId, storedData} objects for matching compositions
   */
  findMatchingCompositions(cartVariantInfo) {
    const matches = [];

    for (const [compositionId, entry] of Object.entries(this.deliveryData)) {
      if (entry.data && entry.data.length > 0) {
        const latestStoredData = entry.data[0]; // Most recent entry
        const storedVariantInfo = latestStoredData.variant_info || [];

        if (this.areVariantInfoSame(cartVariantInfo, storedVariantInfo)) {
          matches.push({
            compositionId: parseInt(compositionId, 10),
            storedData: latestStoredData
          });
          this.logger.debug(
            `Found exact variant match for composition ${compositionId}: ${cartVariantInfo.length} variants`
          );
        }
      }
    }

    this.logger.info(`Found ${matches.length} compositions with exact variant matches`);
    if (matches.length > 0) {
      this.logger.debug(
        `Exact matches: ${matches.map(m => `composition_${m.compositionId}`).join(", ")}`
      );
    }
    return matches;
  }

  /**
   * Find compositions that contain all the same variant IDs (ignoring quantities)
   * @param {Array} cartVariantInfo - variant_info array from cart response
   * @returns {Array} Array of {compositionId, storedData} objects for compositions with matching variant IDs
   */
  findPartialMatchingCompositions(cartVariantInfo) {
    const matches = [];

    // Extract variant IDs from cart (ignore quantities)
    const cartVariantIds = cartVariantInfo.map(v => v.variant_id).sort((a, b) => a - b);

    for (const [compositionId, entry] of Object.entries(this.deliveryData)) {
      if (entry.data && entry.data.length > 0) {
        const latestStoredData = entry.data[0]; // Most recent entry
        const storedVariantInfo = latestStoredData.variant_info || [];

        // Extract variant IDs from stored data (ignore quantities)
        const storedVariantIds = storedVariantInfo.map(v => v.variant_id).sort((a, b) => a - b);

        // Check if variant ID arrays are the same
        if (JSON.stringify(cartVariantIds) === JSON.stringify(storedVariantIds)) {
          matches.push({
            compositionId: parseInt(compositionId, 10),
            storedData: latestStoredData
          });
          this.logger.debug(
            `Found partial variant match for composition ${compositionId}: variant IDs [${cartVariantIds.join(", ")}]`
          );
        }
      }
    }

    this.logger.info(
      `Found ${matches.length} compositions with matching variant IDs (partial match)`
    );
    if (matches.length > 0) {
      this.logger.debug(
        `Partial matches: ${matches.map(m => `composition_${m.compositionId}`).join(", ")}`
      );
    }
    return matches;
  }

  /**
   * Compare two variant_info arrays to check if they are the same
   * @param {Array} variants1 - First variant array
   * @param {Array} variants2 - Second variant array
   * @returns {boolean} True if both variant arrays are the same
   */
  // eslint-disable-next-line class-methods-use-this
  areVariantInfoSame(variants1, variants2) {
    // Handle empty/null cases
    if (!variants1 && !variants2) return true;
    if (!variants1 || !variants2) return false;

    // Simple JSON comparison
    return JSON.stringify(variants1) === JSON.stringify(variants2);
  }

  /**
   * Store delivery data without discrepancy detection (for partial matches)
   * - Maintains array of last 6 entries
   * - No event firing or discrepancy detection
   * - Preserves metadata from previous entries when new data lacks it
   */
  upsertEntryOnly(compositionId, newDataItem) {
    const storageKey = `${compositionId}`;
    const existingEntry = this.deliveryData[storageKey];

    if (existingEntry && Array.isArray(existingEntry.data) && existingEntry.data.length > 0) {
      const previousDataItem = existingEntry.data[0];

      // Preserve selected attributes from previous entry when the new value is unknown/empty
      if (!newDataItem.color || newDataItem.color === "Unknown") {
        newDataItem.color = previousDataItem.color || newDataItem.color || "Unknown";
      }
      if (!newDataItem.variant_info || newDataItem.variant_info.length === 0) {
        newDataItem.variant_info = previousDataItem.variant_info || [];
      }
      if (!newDataItem.zipcode || newDataItem.zipcode === "Unknown") {
        newDataItem.zipcode = this.extractZipcodeFromDOM() || previousDataItem.zipcode || "Unknown";
      }
      if (!newDataItem.store_id) {
        newDataItem.store_id = this.getStoreIdFromDOM() || previousDataItem.store_id || null;
      }
      if (!newDataItem.size && previousDataItem.size) {
        newDataItem.size = previousDataItem.size;
      }
      if (!newDataItem.quantity && previousDataItem.quantity) {
        newDataItem.quantity = previousDataItem.quantity;
      }

      // Just store the data without any discrepancy checking
      const updatedDataArray = [newDataItem, ...existingEntry.data];
      if (updatedDataArray.length > 6) updatedDataArray.splice(6);
      this.deliveryData[storageKey] = { data: updatedDataArray, lastUpdated: Date.now() };

      this.logger.debug(
        `Data stored for composition ${compositionId} (partial match - no discrepancy check)`
      );
    } else {
      this.deliveryData[storageKey] = { data: [newDataItem], lastUpdated: Date.now() };
      this.logger.debug(`Initial data stored for composition: ${compositionId} (partial match)`);
    }

    this.enforceDeliveryDataCap();
  }

  /**
   * Centralized upsert and discrepancy detection for any response type
   * - Maintains array of last 6 entries
   * - Fires event if business line matches and date changes
   * - Preserves metadata intelligently based on page transitions
   */
  upsertEntryAndMaybeFire(compositionId, newDataItem) {
    // Validate date before processing
    if (!newDataItem.date) {
      this.logger.warn(`Invalid date for composition ${compositionId}, skipping upsert`);
      this.enforceDeliveryDataCap();
      return;
    }
    const dateObj =
      newDataItem.date instanceof Date ? newDataItem.date : new Date(newDataItem.date);
    if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) {
      this.logger.warn(`Invalid date for composition ${compositionId}, skipping upsert`);
      this.enforceDeliveryDataCap();
      return;
    }

    const storageKey = String(compositionId);
    const existingEntry = this.deliveryData[storageKey];

    if (existingEntry && Array.isArray(existingEntry.data) && existingEntry.data.length > 0) {
      const previousDataItem = existingEntry.data[0];

      // ✅ STORE ORIGINAL VALUES BEFORE PRESERVATION (for accurate quantity calculation)
      const originalFromVariantInfo = previousDataItem.variant_info || [];
      const originalToVariantInfo = newDataItem.variant_info || [];

      // ✅ PRESERVE FIRST (MAIN logic)
      if (!newDataItem.color || newDataItem.color === "Unknown") {
        newDataItem.color = previousDataItem.color || newDataItem.color || "Unknown";
      }
      if (!newDataItem.variant_info || newDataItem.variant_info.length === 0) {
        newDataItem.variant_info = previousDataItem.variant_info || [];
      }
      if (!newDataItem.zipcode || newDataItem.zipcode === "Unknown") {
        newDataItem.zipcode = this.extractZipcodeFromDOM() || previousDataItem.zipcode || "Unknown";
      }
      if (!newDataItem.store_id) {
        newDataItem.store_id = this.getStoreIdFromDOM() || previousDataItem.store_id || null;
      }
      if (!newDataItem.size && previousDataItem.size) {
        newDataItem.size = previousDataItem.size;
      }
      if (!newDataItem.quantity && previousDataItem.quantity) {
        newDataItem.quantity = previousDataItem.quantity;
      }

      // ✅ NOW COMPARE (after preservation)
      const currentDateObj = dateObj;
      // Validate previous date before processing
      if (!previousDataItem.date) {
        this.logger.warn(
          `Invalid previous date for composition ${compositionId}, skipping comparison`
        );
        // Treat as no previous date - proceed with upsert but skip comparison
        this.deliveryData[storageKey] = {
          data: [newDataItem],
          lastUpdated: Date.now()
        };
        return;
      }
      const previousDateObj =
        previousDataItem.date instanceof Date
          ? previousDataItem.date
          : new Date(previousDataItem.date);
      if (!(previousDateObj instanceof Date) || Number.isNaN(previousDateObj.getTime())) {
        this.logger.warn(
          `Invalid previous date for composition ${compositionId}, skipping comparison`
        );
        // Treat as no previous date - proceed with upsert but skip comparison
        this.deliveryData[storageKey] = {
          data: [newDataItem],
          lastUpdated: Date.now()
        };
        return;
      }

      const datesAreDifferent = currentDateObj.getTime() !== previousDateObj.getTime();

      // ✅ FIRE EVENT ON ANY DATE CHANGE
      if (datesAreDifferent) {
        const day = 1000 * 60 * 60 * 24;
        const deltaDays = Math.round((currentDateObj.getTime() - previousDateObj.getTime()) / day);
        let direction = "same";
        if (deltaDays > 0) direction = "later";
        else if (deltaDays < 0) direction = "earlier";

        // ✅ CALCULATE QUANTITIES **BEFORE** PRESERVATION (use original values)
        const fromVariantInfo = originalFromVariantInfo;
        const toVariantInfo = originalToVariantInfo;
        const fromQuantityTotal = fromVariantInfo.reduce((sum, v) => sum + (v?.quantity || 0), 0);
        const toQuantityTotal = toVariantInfo.reduce((sum, v) => sum + (v?.quantity || 0), 0);
        const quantityDelta = toQuantityTotal - fromQuantityTotal;
        const quantityChangePercentage =
          fromQuantityTotal > 0 ? (quantityDelta / fromQuantityTotal) * 100 : 0;

        // Calculate comparison flags for enrichment (useful for Mixpanel filtering)
        // ✅ Use ORIGINAL values for comparison flags (to detect actual changes, not preservation artifacts)
        const variantInfoAreSame = this.areVariantInfoSame(
          originalToVariantInfo,
          originalFromVariantInfo
        );
        const variantInfoChanged = !variantInfoAreSame;
        const zipcodesAreSame = newDataItem.zipcode === previousDataItem.zipcode;
        const storeIdsAreSame = newDataItem.store_id === previousDataItem.store_id;
        const businessLinesAreSame = newDataItem.business_line === previousDataItem.business_line;

        const discrepancyData = {
          composition_id: newDataItem.composition_id,
          from_page: previousDataItem.page,
          from_url: previousDataItem.url,
          from_date: previousDateObj,
          from_zipcode: previousDataItem.zipcode,
          from_variant_info: fromVariantInfo,
          from_quantity_total: fromQuantityTotal,
          from_fetched_at: this.ensureISOFormat(previousDataItem.fetched_at),
          from_api_endpoint: previousDataItem.api_endpoint,
          from_api_response: previousDataItem.api_response,
          to_page: newDataItem.page,
          to_url: newDataItem.url,
          to_date: currentDateObj,
          to_zipcode: newDataItem.zipcode,
          to_variant_info: toVariantInfo,
          to_quantity_total: toQuantityTotal,
          to_fetched_at: this.ensureISOFormat(newDataItem.fetched_at),
          to_api_endpoint: newDataItem.api_endpoint,
          to_api_response: newDataItem.api_response,
          quantity_delta: quantityDelta,
          quantity_change_percentage: quantityChangePercentage,
          business_line: newDataItem.business_line || previousDataItem.business_line,
          user_id: newDataItem.user_id,
          user_type: newDataItem.user_type,
          store_id:
            newDataItem.store_id || this.getStoreIdFromDOM() || previousDataItem.store_id || null,
          delta_days: deltaDays,
          direction,

          // ✅ Classification flags for Mixpanel filtering
          variant_info_changed: variantInfoChanged,
          zipcode_changed: !zipcodesAreSame,
          store_id_changed: !storeIdsAreSame,
          business_line_changed: !businessLinesAreSame
        };

        this.fireDiscrepancyEvent(discrepancyData);
      }

      const updatedDataArray = [newDataItem, ...existingEntry.data];
      if (updatedDataArray.length > 6) updatedDataArray.splice(6);
      this.deliveryData[storageKey] = { data: updatedDataArray, lastUpdated: Date.now() };
    } else {
      this.deliveryData[storageKey] = { data: [newDataItem], lastUpdated: Date.now() };
      this.logger.debug(`Initial data stored for composition: ${compositionId}`);
    }

    this.enforceDeliveryDataCap();
  }

  /**
   * Process single composition response (existing format)
   */
  // eslint-disable-next-line max-params
  processSingleCompositionResponse(url, responseData, options = {}) {
    const { requestData = null } = options;
    const compositionId = this.findCompositionId(url, requestData, responseData);
    if (!compositionId) {
      this.logger.debug("No composition_id found in any source, skipping");
      return;
    }

    const rawDeliveryDate = responseData.composition_dates?.shipments?.[0]?.full_date;
    if (!rawDeliveryDate) {
      this.logger.debug("No delivery date in response, skipping");
      return;
    }

    // Parse the delivery date to Date object
    const deliveryDate = this.parseDeliveryDate(rawDeliveryDate);
    if (!deliveryDate) {
      this.logger.warn(`Failed to parse delivery date: ${rawDeliveryDate}`);
      return;
    }

    const pageInfo = this.getCurrentPageInfo();
    const currentPage = this.getPageName();

    this.logger.debug(
      `Processing single composition delivery date "${deliveryDate}" for composition ${compositionId} on page "${currentPage}"`
    );

    // Get variant information from mixpanel data
    let variantInfo = this.getVariantInfo(
      compositionId,
      responseData.mixpanel,
      responseData.mixpanel?.business_line
    );

    // ✅ FIX: For PDP, force quantity to 1 (ignore API quantities which reflect cart state)
    if (currentPage === "PDP" && variantInfo.length > 0) {
      variantInfo = variantInfo.map(v => ({
        variant_id: v.variant_id,
        quantity: 1 // Force quantity: 1 for PDP
      }));
      this.logger.debug(`Normalized PDP variant quantities to 1 for composition ${compositionId}`);
    }

    if (
      !responseData.mixpanel?.composition_variants_with_quantities ||
      Object.keys(responseData.mixpanel.composition_variants_with_quantities).length === 0
    ) {
      this.logger.warn(
        `Missing mixpanel.composition_variants_with_quantities for composition ${compositionId} (variant_info length: ${variantInfo.length})`
      );
    }

    // Calculate total quantity from variants
    // Default to 1 when variantInfo is empty (product being viewed has at least quantity 1)
    const totalQuantity =
      variantInfo.length > 0
        ? variantInfo.reduce((sum, variant) => sum + (variant.quantity || 0), 0)
        : 1;

    const newDataItem = {
      business_line:
        responseData.mixpanel?.business_line || this.inferBusinessLineFromURL(pageInfo.url),
      color: responseData.mixpanel?.composition || "Unknown",
      composition_id: parseInt(compositionId, 10),
      date: deliveryDate,
      page: currentPage,
      time_left: responseData.composition_dates?.timeleft || "",
      url: pageInfo.url,
      quantity: totalQuantity,
      size: responseData.composition_dates?.size || 1,
      user_id: DeliveryDateMonitor.getUserIdentifier().id,
      user_type: DeliveryDateMonitor.getUserIdentifier().type,
      variant_info: variantInfo,
      zipcode: (() => {
        const mixpanelZipcode = responseData.mixpanel?.zipcode;
        if (mixpanelZipcode && mixpanelZipcode !== "Unknown" && mixpanelZipcode.trim()) {
          return mixpanelZipcode;
        }
        return this.extractZipcodeFromDOM() || "Unknown";
      })(),
      store_id: this.getStoreIdFromDOM() || null,
      fetched_at: new Date().toISOString(),
      api_response: responseData,
      api_endpoint: url
    };
    this.upsertEntryAndMaybeFire(compositionId, newDataItem);
    this.saveStorageData();
  }

  /**
   * Process multi-composition response (new format with estimates)
   */
  processMultiCompositionResponse(url, responseData) {
    if (!responseData.success || !responseData.estimates) {
      this.logger.warn("Invalid multi-composition response format");
      return;
    }

    const pageInfo = this.getCurrentPageInfo();
    const currentPage = this.getPageName();
    const { estimates } = responseData;
    // Use business_line from response if available, otherwise infer from URL
    const businessLine = responseData.business_line || this.inferBusinessLineFromURL(pageInfo.url);
    let processedCount = 0;

    this.logger.debug(
      `Processing multi-composition response with ${Object.keys(estimates).length} compositions, business_line: ${businessLine}`
    );

    // Process each composition in the estimates
    for (const [compositionId, estimate] of Object.entries(estimates)) {
      const existingEntry = this.deliveryData[String(compositionId)];
      const previousDataItem =
        existingEntry && Array.isArray(existingEntry.data) && existingEntry.data.length > 0
          ? existingEntry.data[0]
          : null;
      const rawDeliveryDate = estimate.date;
      const timeLeft = estimate.timeleft;

      if (!rawDeliveryDate) {
        this.logger.debug(`No delivery date for composition ${compositionId}, skipping`);
        processedCount += 0;
      } else {
        // Parse the delivery date to Date object
        const deliveryDate = this.parseDeliveryDate(rawDeliveryDate);
        if (!deliveryDate) {
          this.logger.warn(
            `Failed to parse delivery date for composition ${compositionId}: ${rawDeliveryDate}`
          );
        } else {
          const newDataItem = {
            business_line: businessLine || estimate.business_line,
            color: "Unknown", // Not available in multi-composition response
            composition_id: parseInt(compositionId, 10),
            date: deliveryDate,
            page: currentPage,
            time_left: timeLeft || "",
            url: pageInfo.url,
            quantity: 1,
            size: 1,
            user_id: DeliveryDateMonitor.getUserIdentifier().id,
            user_type: DeliveryDateMonitor.getUserIdentifier().type,
            variant_info: previousDataItem?.variant_info || [], // Not available in multi-composition response
            zipcode: this.extractZipcodeFromDOM() || "Unknown", // Not available in multi-composition response
            store_id: this.getStoreIdFromDOM() || null,
            fetched_at: new Date().toISOString(),
            one_ship_day: estimate.one_ship_day || false,
            api_response: responseData,
            api_endpoint: url
          };
          this.upsertEntryAndMaybeFire(compositionId, newDataItem);
          processedCount += 1;
        }
      }
    }

    this.logger.info(`Processed ${processedCount} compositions from multi-response`);
    this.saveStorageData();
  }

  /**
   * Find the fastest (earliest delivery_date) option in an order
   * @param {Object} orderData - Single order object from shipments.orders
   * @returns {Object|null} The option with earliest delivery_date, or null if none found
   */
  findFastestOptionPerOrder(orderData) {
    const options = orderData.options || [];

    if (options.length === 0) {
      this.logger.debug("No options found in order, returning null");
      return null;
    }

    let fastestOption = null;
    let earliestDate = null;

    for (const option of options) {
      const deliveryDateStr = option.delivery_date;
      if (deliveryDateStr) {
        // Parse the delivery date
        const deliveryDate = this.parseDeliveryDate(deliveryDateStr);
        if (deliveryDate) {
          // Check if this is the earliest date so far
          if (!earliestDate || deliveryDate.getTime() < earliestDate.getTime()) {
            earliestDate = deliveryDate;
            fastestOption = option;
            this.logger.debug(
              `New fastest option found: "${option.title}" with delivery_date "${deliveryDateStr}"`
            );
          }
        } else {
          this.logger.warn(
            `Failed to parse delivery_date "${deliveryDateStr}" for option "${option.title}"`
          );
        }
      } else {
        this.logger.debug(`Option "${option.title}" has no delivery_date, skipping`);
      }
    }

    if (fastestOption) {
      this.logger.info(
        `Fastest option for order "${orderData.title || orderData.id}": "${fastestOption.title}" (${this.formatDeliveryDate(earliestDate)})`
      );
    } else {
      this.logger.warn("No valid options found in order");
    }

    return fastestOption;
  }

  /**
   * Process cart delivery response (cart API format)
   */
  processCartDeliveryResponse(url, responseData) {
    this.logger.info(`Processing cart delivery response for ${url}`);
    if (!responseData.shipments || !responseData.delivery || !responseData.delivery_notification) {
      this.logger.warn("Invalid cart delivery response format");
      return;
    }

    const pageInfo = this.getCurrentPageInfo();
    const currentPage = this.getPageName();
    let processedCount = 0;

    try {
      // Find all composition IDs and delivery dates from shipments
      const shipmentOrders = responseData.shipments?.orders || {};

      for (const orderData of Object.values(shipmentOrders)) {
        // Find the fastest (earliest delivery_date) option for this order
        const fastestOption = this.findFastestOptionPerOrder(orderData);

        if (fastestOption) {
          const variantsWithQuantities = fastestOption.variants_with_quantities;

          if (variantsWithQuantities && Object.keys(variantsWithQuantities).length > 0) {
            // Convert to variant_info format for comparison
            const cartVariantInfo = this.convertVariantsToVariantInfo(variantsWithQuantities);
            if (cartVariantInfo.length > 0) {
              this.logger.debug(
                `Cart variants: ${cartVariantInfo.map(v => `${v.variant_id}:${v.quantity}`).join(", ")}`
              );

              // Find compositions with matching variant_info (exact match first)
              let matchingCompositions = this.findMatchingCompositions(cartVariantInfo);
              let isPartialMatch = false;

              // If no exact matches found, try partial matching (same variant IDs, ignore quantities)
              if (matchingCompositions.length === 0) {
                this.logger.debug(
                  "No exact variant matches found, trying partial matching (same variant IDs)"
                );
                matchingCompositions = this.findPartialMatchingCompositions(cartVariantInfo);
                isPartialMatch = true; // Track that this is a partial match
              }

              if (matchingCompositions.length > 0) {
                // Parse delivery date from cart data
                // Priority: delivery_date (ISO format) is most reliable
                const isoDeliveryDate = fastestOption.delivery_date;
                let cartDeliveryDate = null;

                if (isoDeliveryDate) {
                  cartDeliveryDate = this.parseDeliveryDate(isoDeliveryDate);
                }
                // Fallback: Try other date fields if primary fails
                if (!cartDeliveryDate && fastestOption.arrival_date) {
                  cartDeliveryDate = this.parseDeliveryDate(fastestOption.arrival_date);
                }

                if (cartDeliveryDate) {
                  // Compare delivery dates for each matching composition
                  for (const match of matchingCompositions) {
                    const { compositionId, storedData } = match;
                    const storedDeliveryDate =
                      storedData.date instanceof Date ? storedData.date : new Date(storedData.date);

                    const datesAreDifferent =
                      cartDeliveryDate.getTime() !== storedDeliveryDate.getTime();

                    // Extract location_codes from shipments (warehouse codes)
                    const locationCodes = [];
                    if (fastestOption.shipments && Array.isArray(fastestOption.shipments)) {
                      for (const shipment of fastestOption.shipments) {
                        if (
                          shipment.location_code &&
                          !locationCodes.includes(shipment.location_code)
                        ) {
                          locationCodes.push(shipment.location_code);
                        }
                      }
                    }

                    // Store cart delivery data for this composition using the same structure
                    const cartDataItem = {
                      business_line: storedData.business_line, // Preserve from stored data
                      color: storedData.color, // Preserve from stored data
                      composition_id: compositionId,
                      date: cartDeliveryDate,
                      page: currentPage,
                      time_left: fastestOption.valid_until_time || "",
                      url: pageInfo.url,
                      quantity: cartVariantInfo.reduce((sum, v) => sum + v.quantity, 0), // Total quantity from variants
                      size: storedData.size || 1, // Preserve from stored data
                      user_id: DeliveryDateMonitor.getUserIdentifier().id,
                      user_type: DeliveryDateMonitor.getUserIdentifier().type,
                      variant_info: cartVariantInfo,
                      zipcode: this.extractZipcodeFromDOM() || storedData.zipcode || "Unknown",
                      store_id: storedData.store_id || this.getStoreIdFromDOM() || null,
                      fetched_at: new Date().toISOString(),
                      shipping_rule_id: fastestOption.rule_id || null,
                      location_codes: locationCodes,
                      api_response: responseData,
                      api_endpoint: url
                    };

                    if (isPartialMatch) {
                      // For partial matches: store data only, NO discrepancy events
                      this.upsertEntryOnly(compositionId, cartDataItem);
                      processedCount += 1;

                      this.logger.info(
                        `Partial variant match for composition ${compositionId}: data stored without discrepancy check (variant IDs match, quantities differ)`
                      );
                    } else {
                      // For exact matches: store data AND check for discrepancies
                      this.upsertEntryAndMaybeFire(compositionId, cartDataItem);
                      processedCount += 1;

                      if (datesAreDifferent) {
                        this.logger.info(
                          `Exact variant match - delivery date discrepancy detected for composition ${compositionId}: ${this.formatDeliveryDate(storedDeliveryDate)} (${storedData.page}) → ${this.formatDeliveryDate(cartDeliveryDate)} (${currentPage})`
                        );
                      } else {
                        this.logger.debug(
                          `Exact variant match - delivery dates match for composition ${compositionId}: ${this.formatDeliveryDate(cartDeliveryDate)} - data stored`
                        );
                      }
                    }
                  }
                } else {
                  this.logger.warn(`Failed to parse delivery date from cart data`);
                }
              } else {
                this.logger.debug("No stored compositions found with matching variants");
              }
            } else {
              this.logger.debug("Failed to convert variants_with_quantities to variant_info");
            }
          } else {
            this.logger.debug("No variants_with_quantities in shipment, skipping");
          }
          // }
        } else if (!fastestOption) {
          this.logger.debug(
            `No valid fastest option found for order "${orderData.title || orderData.id}", skipping`
          );
        } else {
          this.logger.debug(`Fastest option "${fastestOption.title}" has no shipments, skipping`);
        }
      }

      this.logger.info(`Found ${processedCount} delivery date discrepancies in cart response`);
      this.saveStorageData();
    } catch (error) {
      this.logger.error("Error processing cart delivery response:", error);
    }
  }

  /**
   * Fire discrepancy event (debounced entry point)
   */
  fireDiscrepancyEvent(eventData) {
    // Create a unique key for this specific discrepancy event
    const eventKey = this.generateEventKey(eventData);

    this.logger.debug(
      `Queuing discrepancy event for composition_id=${eventData.composition_id}, key=${eventKey}`
    );

    // Store the event data (latest data overwrites previous for same key)
    this.pendingEvents.set(eventKey, eventData);

    // Cap: flush immediately if too many pending events to prevent unbounded growth
    if (this.pendingEvents.size >= 100) {
      this.debouncedProcessor.flush();
    }

    // Trigger the debounced processor
    this.debouncedProcessor();
  }

  /**
   * Process all pending events (called by the debounced processor)
   */
  processPendingEvents() {
    this.logger.debug(`Processing ${this.pendingEvents.size} pending events`);

    // Process all unique pending events
    for (const [eventKey, eventData] of this.pendingEvents.entries()) {
      this.logger.debug(`Firing event for key=${eventKey}`);
      this.actualFireDiscrepancyEvent(eventData);
    }

    // Clear all pending events after processing
    this.pendingEvents.clear();
  }

  /**
   * Generate a unique key for an event to enable deduplication
   */
  // eslint-disable-next-line class-methods-use-this
  generateEventKey(eventData) {
    // Create a key based on composition_id, from_date, to_date, and business_line
    const fromDate = eventData.from_date?.toString() ?? "null";
    const toDate = eventData.to_date?.toString() ?? "null";

    return `${eventData.composition_id}_${fromDate}_${toDate}_${eventData.business_line || "unknown"}`;
  }

  /**
   * Actually fire the discrepancy event (Mixpanel or custom handler)
   */
  actualFireDiscrepancyEvent(eventData) {
    // Format dates for Mixpanel in ISO format (YYYY-MM-DD)
    const formatDateForMixpanel = value => {
      if (value instanceof Date) return this.formatDeliveryDateISO(value, false);
      return value;
    };

    const enrichedData = {
      // Core identification
      composition_id: eventData.composition_id,
      business_line: eventData.business_line,
      user_id: eventData.user_id,
      user_type: eventData.user_type,
      store_id: eventData.store_id,

      // Page transition
      from_page: eventData.from_page,
      to_page: eventData.to_page,
      from_url: eventData.from_url,
      to_url: eventData.to_url,

      // Zipcode
      from_zipcode: eventData.from_zipcode,
      to_zipcode: eventData.to_zipcode,

      // Date change (ISO format for Mixpanel: YYYY-MM-DD)
      from_date: formatDateForMixpanel(eventData.from_date) || "",
      to_date: formatDateForMixpanel(eventData.to_date) || "",
      from_date_with_timezone:
        eventData.from_date instanceof Date
          ? this.formatDeliveryDateISO(eventData.from_date, true)
          : eventData.from_date,
      to_date_with_timezone:
        eventData.to_date instanceof Date
          ? this.formatDeliveryDateISO(eventData.to_date, true)
          : eventData.to_date,
      delta_days: eventData.delta_days,
      direction: eventData.direction,
      from_fetched_at: eventData.from_fetched_at,
      to_fetched_at: eventData.to_fetched_at,
      from_api_endpoint: eventData.from_api_endpoint,
      from_api_response: eventData.from_api_response,
      to_api_endpoint: eventData.to_api_endpoint,
      to_api_response: eventData.to_api_response,

      // ✅ USE VALUES FROM eventData (calculated before preservation)
      from_variant_info: eventData.from_variant_info || [],
      to_variant_info: eventData.to_variant_info || [],
      from_quantity_total: eventData.from_quantity_total || 0,
      to_quantity_total: eventData.to_quantity_total || 0,
      quantity_delta: eventData.quantity_delta || 0,
      quantity_change_percentage: eventData.quantity_change_percentage || 0,

      // ✅ Classification flags for Mixpanel filtering
      variant_info_changed: eventData.variant_info_changed || false,
      zipcode_changed: eventData.zipcode_changed || false,
      store_id_changed: eventData.store_id_changed || false,
      business_line_changed: eventData.business_line_changed || false,

      // Additional fields for Mixpanel
      line_items: [], // To be implemented later
      shipping_rule: "Jiffy Print",
      detected_at: this.formatDeliveryDateISO(new Date(), true), // ISO datetime format
      pageUrl: window.location.href
    };

    this.logger.info("Firing discrepancy event:", enrichedData);

    // Try to use Mixpanel if enabled
    if (this.config.enableMixpanel) {
      try {
        track(this.config.mixpanelEventName, enrichedData);
        this.logger.info(`Mixpanel event "${this.config.mixpanelEventName}" fired successfully`);
      } catch (error) {
        this.logger.error("Failed to send Mixpanel event:", error);
      }
    }

    // Always call custom handler if provided
    if (typeof this.config.onDiscrepancy === "function") {
      try {
        this.config.onDiscrepancy(enrichedData);
      } catch (error) {
        this.logger.error("Custom discrepancy handler failed:", error);
      }
    }
  }

  /**
   * Intercept fetch requests
   */
  interceptFetch() {
    // Store original fetch for restoration in destroy()
    this.originalFetch = window.fetch;
    const that = this;

    window.fetch = function (...args) {
      // Normalize input: fetch(url[, options]) | fetch(Request)
      let url;
      let options = {};
      if (args[0] instanceof Request) {
        url = args[0].url;
        // Avoid accessing non-standard internals; skip body parsing for Request instances
        options = { method: args[0].method };
      } else {
        [url, options = {}] = args;
      }
      // Use the new pattern-aware URL matching
      const isMonitoredEndpoint = that.isMonitoredUrl(url);

      if (isMonitoredEndpoint) {
        that.logger.debug("Intercepted fetch to delivery API:", url);

        // Extract request data if it's a POST request with body
        const requestData = that.safeParseRequestBody(options);

        return that.originalFetch.apply(this, args).then(response => {
          const cloned = response.clone();

          const contentType = cloned.headers.get("content-type") || "";
          const shouldParseJson =
            cloned.ok &&
            cloned.status !== 204 && // No Content
            cloned.status !== 205 && // Reset Content
            cloned.status !== 304 && // Not Modified
            contentType.toLowerCase().includes("json");

          if (shouldParseJson) {
            cloned
              .text()
              .then(text => {
                if (!text || !text.trim()) return;

                const data = JSON.parse(text);
                that.processDeliveryResponse(url, data, requestData);
              })
              .catch(error => that.logger.error("Failed to read/parse fetch response:", error));
          }

          return response;
        });
      }

      return that.originalFetch.apply(this, args);
    };
  }

  /**
   * Intercept XMLHttpRequest
   */
  interceptXHR() {
    // Store original XHR methods for restoration in destroy()
    this.originalXHROpen = XMLHttpRequest.prototype.open;
    this.originalXHRSend = XMLHttpRequest.prototype.send;
    const that = this;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.monitorUrl = url;
      this.monitorMethod = method;
      return that.originalXHROpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function (...args) {
      // Use the new pattern-aware URL matching
      const isMonitoredEndpoint = that.isMonitoredUrl(this.monitorUrl);

      if (isMonitoredEndpoint) {
        that.logger.debug("Intercepted XHR to delivery API:", this.monitorUrl);

        // Store request data if it's a POST request
        const requestData = that.safeParseXHRBody(this.monitorMethod, args[0]);

        this.addEventListener(
          "load",
          function () {
            try {
              const data = JSON.parse(this.responseText);
              that.processDeliveryResponse(this.monitorUrl, data, requestData);
            } catch (error) {
              that.logger.error("Failed to parse XHR response:", error);
            }
          },
          { once: true }
        );
      }

      return that.originalXHRSend.apply(this, args);
    };
  }

  /**
   * Best-effort request body parsing for fetch
   */
  safeParseRequestBody(options = {}) {
    try {
      const method = (options.method || "GET").toUpperCase();
      if (method !== "POST" && method !== "PUT" && method !== "PATCH") return null;
      const { body } = options;
      if (!body) return null;
      if (typeof body === "string") return JSON.parse(body);
      if (body instanceof FormData) {
        const obj = {};
        for (const [key, value] of body.entries()) obj[key] = value;
        return obj;
      }
      if (body instanceof URLSearchParams) {
        const obj = {};
        for (const [key, value] of body.entries()) obj[key] = value;
        return obj;
      }
      // Do not access non-standard internals
    } catch (error) {
      this.logger.debug("Could not parse request body:", error);
    }
    return null;
  }

  /**
   * Best-effort request body parsing for XHR
   */
  // eslint-disable-next-line class-methods-use-this
  safeParseXHRBody(method, arg0) {
    try {
      const upper = (method || "GET").toUpperCase();
      if (upper !== "POST" && upper !== "PUT" && upper !== "PATCH") return null;
      if (!arg0) return null;
      if (typeof arg0 === "string") return JSON.parse(arg0);
      if (arg0 instanceof FormData) {
        const obj = {};
        for (const [key, value] of arg0.entries()) obj[key] = value;
        return obj;
      }
    } catch (error) {
      // Intentionally swallow
    }
    return null;
  }

  /**
   * Evict oldest deliveryData entries when the collection exceeds the cap.
   */
  enforceDeliveryDataCap() {
    const MAX_ENTRIES = 500;
    const keys = Object.keys(this.deliveryData);
    const excess = keys.length - MAX_ENTRIES;
    if (excess <= 0) return;

    // Find and delete the `excess` oldest entries in a single O(n) pass per eviction
    for (let i = 0; i < excess; i += 1) {
      let oldestKey = null;
      let oldestTs = Infinity;
      for (const key of Object.keys(this.deliveryData)) {
        const ts = this.deliveryData[key].lastUpdated || 0;
        if (ts < oldestTs) {
          oldestTs = ts;
          oldestKey = key;
        }
      }
      if (oldestKey !== null) delete this.deliveryData[oldestKey];
    }

    this.logger.debug(`Evicted ${excess} oldest deliveryData entries (cap: ${MAX_ENTRIES})`);
  }

  /**
   * Tear down the monitor: restore intercepted prototypes and cancel pending work.
   */
  destroy() {
    if (!this.initialized) return;

    if (this.originalXHROpen) {
      XMLHttpRequest.prototype.open = this.originalXHROpen;
    }
    if (this.originalXHRSend) {
      XMLHttpRequest.prototype.send = this.originalXHRSend;
    }
    if (this.originalFetch) {
      window.fetch = this.originalFetch;
    }

    this.debouncedProcessor.flush();
    this.debouncedProcessor.cancel();
    this.pendingEvents.clear();

    this.initialized = false;
    this.logger.info("Delivery date monitor destroyed");
  }

  /**
   * Clean up expired entries manually
   */
  cleanupExpiredEntries() {
    const before = Object.keys(this.deliveryData).length;
    this.loadStorageData(); // This will remove expired entries
    const after = Object.keys(this.deliveryData).length;
    const removed = before - after;

    this.logger.info(`Cleanup complete: removed ${removed} expired entries`);
    return removed;
  }

  /**
   * Get current monitor statistics
   */
  getStats() {
    return {
      initialized: this.initialized,
      entriesCount: Object.keys(this.deliveryData).length,
      config: {
        apiEndpoints: this.config.apiEndpoints,
        expiryHours: this.config.expiryHours,
        pagePatterns: Object.keys(this.config.pagePatterns).length
      }
    };
  }
}

// Export factory function for creating monitor instances
export function createDeliveryMonitor(config = {}) {
  return new DeliveryDateMonitor(config);
}

// Auto-initialize with default config if imported directly
let defaultInstance = null;

export function getDefaultMonitor() {
  if (!defaultInstance) {
    defaultInstance = createDeliveryMonitor();
  }
  return defaultInstance;
}

// Intentionally not auto-initializing here to avoid duplicate instances.

export default DeliveryDateMonitor;
